import React, { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "../../utils/toast";

import { api } from "../../api/api";
import AuthContext from "../../contexts/AuthCtx";
import CompleteStep from "../../components/Onboarding/CompleteStep";
import DetailsStep from "../../components/Onboarding/DetailsStep";
import IdentityStep from "../../components/Onboarding/IdentityStep";
import OnboardingLayout from "../../components/Onboarding/OnboardingLayout";
import WelcomeStep from "../../components/Onboarding/WelcomeStep";
import { ONBOARDING } from "../../motion/transitions";

const STORAGE_KEY = "bbr_onboarding_form";

const defaultForm = {
  title: "",
  birthName: "",
  firstName: "",
  nationality: "",
  birthPlace: "",
  birthNumber: "",
  dateOfBirth: new Date("2000-01-01T00:00:00"),
  marketingOptIn: false,
  termsAccepted: false,
};

function loadSavedForm() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultForm;
    const parsed = JSON.parse(saved);
    return {
      ...defaultForm,
      ...parsed,
      dateOfBirth: parsed.dateOfBirth
        ? new Date(parsed.dateOfBirth)
        : defaultForm.dateOfBirth,
    };
  } catch {
    return defaultForm;
  }
}

function Onboarding() {
  const AuthCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState("welcome");
  const [form, setForm] = useState(loadSavedForm);
  const [isLoading, setIsLoading] = useState(false);
  const directionRef = useRef(1);

  useEffect(() => {
    if (AuthCtx.userStatus === "certified") {
      navigate("/dashboard", { replace: true });
      return;
    }
    // Resume CompleteStep after profile save (incl. page refresh)
    if (AuthCtx.userStatus === "pending_certification") {
      setStep("complete");
    }
  }, [AuthCtx.userStatus, navigate]);

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...form,
        dateOfBirth: form.dateOfBirth?.toISOString(),
      })
    );
  }, [form]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const goToStep = (nextStep, direction = 1) => {
    directionRef.current = direction;
    setStep(nextStep);
  };

  const submitProfile = async () => {
    if (!form.dateOfBirth) {
      toast.error("La date de naissance est requise.");
      return;
    }

    setIsLoading(true);
    const user_info = {
      firstName: form.firstName.trim(),
      birthName: form.birthName.trim(),
      nationality: form.nationality,
      title: form.title,
      date_of_birth: moment(form.dateOfBirth).format("YYYY-MM-DD"),
      birth_department_Number: form.birthNumber.trim(),
      place_of_birth: form.birthPlace.trim(),
      marketingOptIn: form.marketingOptIn,
    };

    try {
      await api.post("auth/updateUser", { user_info: JSON.stringify(user_info) });
      // Sync pending_certification — CompleteStep stays mounted until continue
      await AuthCtx.updateHandler();
      sessionStorage.removeItem(STORAGE_KEY);
      goToStep("complete", 1);
    } catch {
      toast.error("Une erreur est survenue. Réessaie.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteContinue = async () => {
    try {
      await api.post("auth/completeOnboarding");
      await AuthCtx.updateHandler();
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Une erreur est survenue. Réessaie.");
    }
  };

  const stepperStep =
    step === "complete" ? 3 : step === "welcome" ? 0 : step;

  const stepVariant =
    directionRef.current < 0 ? ONBOARDING.stepBack : ONBOARDING.stepForward;

  if (step === "welcome") {
    return (
      <OnboardingLayout wide showStepper={false} centered>
        <WelcomeStep
          termsAccepted={form.termsAccepted}
          marketingOptIn={form.marketingOptIn}
          onTermsChange={(v) => updateField("termsAccepted", v)}
          onMarketingChange={(v) => updateField("marketingOptIn", v)}
          onContinue={() => goToStep(1, 1)}
        />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout showStepper currentStep={stepperStep}>
      <div className="relative min-h-[280px] overflow-hidden">
        <AnimatePresence mode="wait" custom={directionRef.current}>
          <motion.div
            key={String(step)}
            initial={stepVariant.initial}
            animate={stepVariant.animate}
            exit={stepVariant.exit}
            transition={stepVariant.transition}
          >
            {step === 1 && (
              <IdentityStep
                form={form}
                onChange={updateField}
                onContinue={() => goToStep(2, 1)}
              />
            )}

            {step === 2 && (
              <DetailsStep
                form={form}
                onChange={updateField}
                onDateChange={(date) => updateField("dateOfBirth", date)}
                onBack={() => goToStep(1, -1)}
                onContinue={submitProfile}
                isLoading={isLoading}
              />
            )}

            {step === "complete" && (
              <CompleteStep onContinue={handleCompleteContinue} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </OnboardingLayout>
  );
}

export default Onboarding;
